import { useCallback, useEffect, useRef, useState } from "react";
import { useAlerts } from "../../provider/alerts";
import { analyticsService } from "../analytics";
import { useScreenLock } from "../screen-lock";
import { pushNotificationsService } from "./service";
import { PushPermissionStatus } from "./types";
import { pushNotificationsErrorAlert } from "./utils";

// Re-arm screen lock after the OS permission prompt has fully dismissed.
const SCREEN_LOCK_REENABLE_DELAY_MS = 1000;
export const useNotificationPermission = () => {
  const {
    disableScreenLock,
    enableScreenLock
  } = useScreenLock();
  const {
    showAlertDialog
  } = useAlerts();
  const [permissionStatus, setPermissionStatus] = useState<PushPermissionStatus | null>(null);
  const screenLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkPermission = useCallback(async () => {
    try {
      const response = await pushNotificationsService.getPermissionStatus();
      setPermissionStatus(response.success ? response.permissionStatus : PushPermissionStatus.NotDetermined);
    } catch (e) {
      console.error("Error checking notification status", e);
      setPermissionStatus(PushPermissionStatus.NotDetermined);
    }
  }, []);
  const allowPermissions = useCallback(async () => {
    try {
      analyticsService.trackEvent("ClickedEnableNotifications", {});

      // Check if permissions are already denied
      if (permissionStatus === PushPermissionStatus.Denied) {
        analyticsService.trackEvent("ClickedEnableNotificationsAlreadyDenied", {});
        analyticsService.trackEvent("ShowedNotificationErrorAlert", {});
        showAlertDialog(pushNotificationsErrorAlert());
        return false;
      }
      disableScreenLock();
      const status = await pushNotificationsService.showPermissionStatusPrompt();
      if (!status.success) {
        // Error, this shouldn't happen but give user a chance to activate
        analyticsService.trackEvent("ClickedEnableNotificationsError", {});
        analyticsService.trackEvent("ShowedNotificationErrorAlert", {});
        showAlertDialog(pushNotificationsErrorAlert());
        return false;
      }
      if (status.permissionStatus === PushPermissionStatus.Granted) {
        analyticsService.trackEvent("ClickedEnableNotificationsSuccess", {});
      } else {
        analyticsService.trackEvent("ClickedEnableNotificationsDenied", {});
      }

      // Refresh permission status
      await checkPermission();
      // True means notifications are actually on — a successful prompt where
      // the user chose Deny returns false.
      return status.permissionStatus === PushPermissionStatus.Granted;
    } catch (e) {
      analyticsService.trackEvent("ClickedEnableNotificationsError", {});
      console.error("[AllowNotifications] Error allowing permissions", e);
      return false;
    } finally {
      // Clear any existing timeout before setting a new one
      if (screenLockTimeoutRef.current) {
        clearTimeout(screenLockTimeoutRef.current);
      }
      screenLockTimeoutRef.current = setTimeout(() => {
        enableScreenLock();
        screenLockTimeoutRef.current = null;
      }, SCREEN_LOCK_REENABLE_DELAY_MS);
    }
  }, [disableScreenLock, enableScreenLock, showAlertDialog, permissionStatus, checkPermission]);
  useEffect(() => {
    void checkPermission();
  }, [checkPermission]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (screenLockTimeoutRef.current) {
        clearTimeout(screenLockTimeoutRef.current);
      }
    };
  }, []);
  return {
    permissionStatus,
    refetch: checkPermission,
    allowPermissions
  };
};